import { Directive, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Params, Router } from '@angular/router';
import { LoadingMetadata } from '@app/shared/store/standard-entity.state';
import { routeParamsFromRoute } from '@app/shared/utils/route-utils';
import { State } from '@app/store/reducers';
import { Action, Store } from '@ngrx/store';
import { DateTime, Duration, DurationLikeObject } from 'luxon';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, withLatestFrom } from 'rxjs/operators';

export type UrlPathSegment = string | { [paramName: string]: string | number };

/**
 * Contains common functions for interacting with the store and router
 */
@Directive()
 
export abstract class StandardContainerComponent implements OnDestroy {
  protected readonly store = inject(Store);
  protected readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);

  protected requested: { [key: string]: boolean } = {};

  protected destroyed$ = new Subject<void>();

  ngOnDestroy() {
    this.destroyed$.next();
  }

  protected flattenRouteParams(
    snapshot: ActivatedRouteSnapshot,
    params: Params = {},
    depth = 0,
  ): Params {
    if (depth === 0 && snapshot !== snapshot.root) {
      return this.flattenRouteParams(snapshot.root);
    }

    Object.getOwnPropertyNames(snapshot.params).forEach((key) => {
      params[key] = snapshot.params[key];
    });

    if (snapshot.firstChild) {
      return this.flattenRouteParams(snapshot.firstChild, params, depth + 1);
    }

    return params;
  }

  protected removeCurrentRouteParameters(paramNames: string[]): void {
    let currentSegmentIndex = -1;
    let node = this.route.snapshot;
    while (node != null) {
      currentSegmentIndex += node.url.length;
      node = node.parent;
    }

    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children.primary.segments;
    const segment = segments[currentSegmentIndex];
    for (const paramName of paramNames) {
      if (segment.parameters[paramName]) {
        delete segment.parameters[paramName];
      }
    }

    this.router.navigateByUrl(this.router.serializeUrl(urlTree));
  }

  /**
   * If a key is present in @param params, it will be set in the route as follows:
   *   - if nullish, the parameter will be removed from the route
   *   - otherwise, the parameter value will be set in the route
   *
   * Existing route parameters whose key is not present in @param params will be left alone.
   */
  protected setCurrentRouteParameters(params: {[key: string]: string }): void {
    let currentSegmentIndex = -1;
    let node = this.route.snapshot;
    while (node != null) {
      currentSegmentIndex += node.url.length;
      node = node.parent;
    }

    const urlTree = this.router.parseUrl(this.router.url);
    if ('primary' in urlTree.root.children) {
      const segments = urlTree.root.children.primary.segments;
      const segment = segments[currentSegmentIndex];

      for (const key in params) {
        if (params[key] == null) {
          if (segment.parameters[key] != null) {
            delete segment.parameters[key];
          }
          delete params[key];
        }
      }

      Object.assign(segment.parameters, params);
    }
    this.router.navigateByUrl(this.router.serializeUrl(urlTree));
  }

  protected setCurrentRouteParameter(paramName: string, paramValue: string): void {
    let currentSegmentIndex = -1;
    let node = this.route.snapshot;
    while (node != null) {
      currentSegmentIndex += node.url.length;
      node = node.parent;
    }

    const param = {};
    param[paramName] = paramValue;

    const urlTree = this.router.parseUrl(this.router.url);
    const segments = urlTree.root.children.primary.segments;
    const segment = segments[currentSegmentIndex];
    Object.assign(segment.parameters, param);
    this.router.navigateByUrl(this.router.serializeUrl(urlTree));
  }

  /**
   * Returns an Observable from the Store based on @param dataSelect.
   *
   * As a side effect, it also dispatches @param action to the Store
   * in the following circumstances:
   * - the data is not present in the Store
   * - it has been longer than defaultMaxAge since the data was loaded into the Store
   * unless an attempt to load the data was initiated within the last 5 seconds.
   */
  protected selectAndLoad<S extends State, T>(
    dataSelect: (source$: Observable<S>) => Observable<T>,
    loadedMetadataSelect: (source$: Observable<S>) => Observable<LoadingMetadata>,
    action: Action,
  ): Observable<T> {
    return this.selectAndLoadIfOlderThan(dataSelect, loadedMetadataSelect, action);
  }

  /**
   * Returns an Observable from the Store based on @param dataSelect.
   *
   * As a side effect, it also dispatches @param action to the Store
   * in the following circumstances:
   * - the data is not present in the Store
   * - it has been longer than defaultMaxAge since the data was loaded into the Store
   * - it has been longer than @param maxAge since the data was loaded into the Store
   * unless an attempt to load the data was initiated within the last 5 seconds.
   *
   * Eventually, this should replace both selectIfNotLoadedAndNotRequested and
   * selectWithPropsIfNotLoadedAndNotRequested.
   */
  protected selectAndLoadIfOlderThan<S extends State, T>(
    dataSelect: (source$: Observable<S>) => Observable<T>,
    loadedMetadataSelect: (source$: Observable<S>) => Observable<LoadingMetadata>,
    action: Action,
    maxAge?: Duration | DurationLikeObject,
  ): Observable<T> {
    return this.store.pipe(
      takeUntil(this.destroyed$),
      dataSelect,
      withLatestFrom(this.store.pipe(loadedMetadataSelect)),
      map(([obj, loadedMetadata]) => {
        const loadingAt = loadedMetadata?.loadingAt;
        const loadedAt = loadedMetadata?.loadedAt;
        maxAge =
          maxAge != null
            ? Duration.isDuration(maxAge)
              ? maxAge
              : Duration.fromObject(maxAge)
            : loadedMetadata?.defaultMaxAge;
        if (
          (!loadingAt || DateTime.local().diff(loadingAt).valueOf() > 5000) &&
          (maxAge.valueOf() === 0 ||
            !loadedAt ||
            !DateTime.isDateTime(loadedAt) ||
            !loadedAt.isValid ||
            DateTime.local().diff(loadedAt) > maxAge)
        ) {
          this.store.dispatch(action);
        }

        return obj;
      }),
    );
  }

  protected getFirstRouteParam<T>(paramName: string): Observable<T> {
    return routeParamsFromRoute(this.route).pipe(
      takeUntil(this.destroyed$),
      map((paramsArray) => {
        const params = paramsArray.find(p => !!p[paramName]);

        if (params) {
          return params[paramName];
        }

        return params;
      }),
      map((param) => {
        if (param == null || isNaN(+param)) {
          return param;
        }

        return +param;
      }),
    );
  }
}
